import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  useGetTodayDropQuery,
  useGetLobbyTodayQuery,
  useLaunchDropNowMutation,
  useResetDropDayMutation,
  useBlockDropUserMutation,
  useUnblockDropUserMutation,
} from "../../api/respectCardsApi";
import { useConfirm } from "../../../../ui/components/ConfirmDialog";

/**
 * cards-drop-lobby phase 10 — admin drop control.
 *
 * Two operator actions backed by the phase-5 admin routes
 * (`/api/v2/respect/admin/cards/drop/{launch,block,unblock}`):
 *  - launch today's drop now (behind a confirm, since it notifies everyone), and
 *  - block / unblock a user from today's pull.
 *
 * Situational awareness reads from two existing queries: `GET /cards/drop/today`
 * (scheduled open time, open state, total claims) and `GET /cards/drop/lobby/today`
 * (live lobby status + participant count; 204 → null when no lobby exists yet).
 *
 * NOTE on the blocked list: the public lobby snapshot intentionally does NOT
 * expose `blockedUserIds` (only the viewer's own `youAreBlocked`), so there is no
 * server field to seed a "currently blocked" list from on load. We therefore track
 * blocks made in THIS session from the block/unblock mutation responses (each
 * echoes the resolved username). A page reload starts the list empty even if
 * blocks persist server-side; surfacing the canonical list would need a backend
 * change (out of this phase's frontend-only scope).
 */

const card: React.CSSProperties = {
  background: "#fff",
  padding: 16,
  borderRadius: 8,
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
  marginBottom: 16,
};

const sectionTitle: React.CSSProperties = {
  fontSize: "1.1rem",
  color: "#333",
  marginBottom: 12,
};

const subTitle: React.CSSProperties = {
  fontSize: "0.95rem",
  color: "#333",
  margin: "0 0 8px",
};

const help: React.CSSProperties = {
  fontSize: "0.82rem",
  color: "#666",
  lineHeight: 1.5,
  margin: "0 0 12px",
};

const btn: React.CSSProperties = {
  fontSize: "0.82rem",
  padding: "6px 12px",
  borderRadius: 6,
  border: "1px solid #c7c1e0",
  background: "#fff",
  color: "#3730a3",
  cursor: "pointer",
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: "#4f46e5",
  borderColor: "#4f46e5",
  color: "#fff",
};

const btnDanger: React.CSSProperties = {
  ...btn,
  background: "#ffebee",
  borderColor: "#c62828",
  color: "#c62828",
};

const inputCss: React.CSSProperties = {
  fontSize: "0.85rem",
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #d1d5db",
};

const tile: React.CSSProperties = {
  border: "1px solid #ece6f7",
  borderRadius: 8,
  background: "#faf7ff",
  padding: 12,
};

const kicker: React.CSSProperties = {
  fontSize: "0.72rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "#6d5a9a",
  marginBottom: 6,
};

const value: React.CSSProperties = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "#2e2550",
};

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

interface BlockedEntry {
  userId: string;
  username: string;
}

const DropControlTab: React.FC = () => {
  const confirm = useConfirm();
  const { data: drop, isLoading: dropLoading, refetch: refetchDrop } = useGetTodayDropQuery();
  const { data: lobbyData, isLoading: lobbyLoading } = useGetLobbyTodayQuery();
  const [launchDrop, launchState] = useLaunchDropNowMutation();
  const [resetDrop, resetState] = useResetDropDayMutation();
  const [blockUser, blockState] = useBlockDropUserMutation();
  const [unblockUser, unblockState] = useUnblockDropUserMutation();

  const [blockUsername, setBlockUsername] = useState("");
  const [blockReason, setBlockReason] = useState("");
  // Session-only blocked list — see the file docblock for why we can't read it
  // from the snapshot. Keyed by userId so re-blocking the same user de-dupes.
  const [blocked, setBlocked] = useState<BlockedEntry[]>([]);

  const lobby = lobbyData?.lobby ?? null;

  const lobbyStatus = useMemo(() => {
    if (lobbyLoading) return "loading…";
    if (!lobby) return "no lobby yet";
    return lobby.status;
  }, [lobby, lobbyLoading]);

  const handleLaunch = async () => {
    const ok = await confirm({
      title: "Launch drop now?",
      message:
        "This opens today's drop immediately and sends the \"pool is open\" notification to everyone. It cannot be un-sent.",
      confirmLabel: "Launch now",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const res = await launchDrop().unwrap();
      toast.success(
        `drop launched (${res.lobby.status})${res.notified ? " — notification sent" : " — already announced"}`,
      );
    } catch (e) {
      toast.error("failed to launch drop");
    }
  };

  const handleReset = async () => {
    const ok = await confirm({
      title: "Reset today's drop?",
      message:
        "DELETES today's lobby + drop record so the day can re-run cleanly. Cards already pulled into user inventories stay (CardInstance docs aren't touched). After this, set a new open time in the Schedule tab.",
      confirmLabel: "Reset",
      tone: "danger",
    });
    if (!ok) return;
    try {
      const res = await resetDrop().unwrap();
      toast.success(
        `reset — lobby ${res.deletedLobby ? "cleared" : "n/a"}, drop ${
          res.deletedDrop ? "cleared" : "n/a"
        }, schedule=${res.scheduleStatus}`,
      );
      void refetchDrop();
    } catch (e) {
      // Surface the backend's actual error so deploy-lag / 404 vs server errors
      // are obvious (matches the block/unblock pattern).
      const msg =
        e && typeof e === "object" && "data" in e
          ? (e as { data?: { message?: string } }).data?.message
          : undefined;
      const status =
        e && typeof e === "object" && "status" in e
          ? (e as { status?: number | string }).status
          : undefined;
      toast.error(msg || `failed to reset drop${status ? ` (${status})` : ""}`);
    }
  };

  const handleBlock = async () => {
    const username = blockUsername.trim();
    if (!username) {
      toast.error("enter a username");
      return;
    }
    try {
      const res = await blockUser({
        username,
        reason: blockReason.trim() || undefined,
      }).unwrap();
      setBlocked((prev) => {
        const next = prev.filter((b) => b.userId !== res.userId);
        return [...next, { userId: res.userId, username: res.username }];
      });
      toast.success(
        res.changed ? `blocked ${res.username}` : `${res.username} was already blocked`,
      );
      setBlockUsername("");
      setBlockReason("");
    } catch (e) {
      const msg =
        e && typeof e === "object" && "data" in e
          ? (e as { data?: { message?: string } }).data?.message
          : undefined;
      toast.error(msg || "failed to block user");
    }
  };

  const handleUnblock = async (username: string, userId: string) => {
    try {
      const res = await unblockUser({ username }).unwrap();
      setBlocked((prev) => prev.filter((b) => b.userId !== (res.userId || userId)));
      toast.success(
        res.changed ? `unblocked ${res.username}` : `${res.username} was not blocked`,
      );
    } catch (e) {
      const msg =
        e && typeof e === "object" && "data" in e
          ? (e as { data?: { message?: string } }).data?.message
          : undefined;
      toast.error(msg || "failed to unblock user");
    }
  };

  return (
    <div>
      <section style={card}>
        <h2 style={sectionTitle}>Drop control</h2>
        <p style={help}>
          launch today&apos;s drop ahead of its scheduled open, and block / unblock
          individual users from pulling. these call the same backend control plane
          the sitebot uses — launching notifies everyone, so use it deliberately.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={tile}>
            <div style={kicker}>Drop state</div>
            <div style={value}>
              {dropLoading ? "loading…" : drop?.isOpen ? "open" : "not open"}
            </div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              date: {drop?.date ?? "—"}
            </div>
          </div>
          <div style={tile}>
            <div style={kicker}>Scheduled open</div>
            <div style={value}>{formatDateTime(drop?.opensAt)}</div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              closes: {formatDateTime(drop?.closesAt)}
            </div>
          </div>
          <div style={tile}>
            <div style={kicker}>Lobby</div>
            <div style={value}>{lobbyStatus}</div>
            <div style={{ fontSize: "0.82rem", color: "#555" }}>
              {lobby ? `${lobby.participantCount} gathered` : "—"}
              {lobby?.simulated ? " · simulated" : ""}
            </div>
          </div>
          <div style={tile}>
            <div style={kicker}>Claims today</div>
            <div style={value}>{drop?.totalClaims ?? 0}</div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            style={btnPrimary}
            onClick={handleLaunch}
            disabled={launchState.isLoading}
          >
            {launchState.isLoading ? "launching…" : "Launch drop now"}
          </button>
          <button
            type="button"
            style={btnDanger}
            onClick={handleReset}
            disabled={resetState.isLoading}
            title="Wipe today's lobby + drop record so the day can re-run. Keeps user card inventories."
          >
            {resetState.isLoading ? "resetting…" : "Reset today"}
          </button>
          <button
            type="button"
            style={btn}
            onClick={() => void refetchDrop()}
            disabled={dropLoading}
          >
            refresh
          </button>
        </div>
      </section>

      <section style={card}>
        <h3 style={subTitle}>Block a user from today&apos;s pull</h3>
        <p style={help}>
          blocked users are rejected at lobby join AND at pull (403). a reason is
          optional and is logged server-side. the list below reflects blocks made in
          this session.
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <input
            type="text"
            value={blockUsername}
            onChange={(e) => setBlockUsername(e.target.value)}
            placeholder="username"
            style={{ ...inputCss, minWidth: 160 }}
            aria-label="username to block"
          />
          <input
            type="text"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="reason (optional)"
            style={{ ...inputCss, minWidth: 220, flex: 1 }}
            aria-label="block reason"
          />
          <button
            type="button"
            style={btnDanger}
            onClick={handleBlock}
            disabled={blockState.isLoading || !blockUsername.trim()}
          >
            {blockState.isLoading ? "blocking…" : "Block"}
          </button>
        </div>

        {blocked.length === 0 ? (
          <p style={{ fontSize: "0.85rem", color: "#666", margin: 0 }}>
            no users blocked this session.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {blocked.map((b) => (
              <li
                key={b.userId}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "8px 12px",
                  border: "1px solid #f0d4d4",
                  borderRadius: 6,
                  marginBottom: 8,
                  background: "#fff5f5",
                }}
              >
                <span style={{ fontSize: "0.88rem", color: "#7a2222" }}>
                  🚫 <strong>{b.username}</strong>
                </span>
                <button
                  type="button"
                  style={btn}
                  onClick={() => void handleUnblock(b.username, b.userId)}
                  disabled={unblockState.isLoading}
                >
                  unblock
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default DropControlTab;
